import { validYouTubeChannelId } from './identity';
import type { YouTubeConfig } from './types';

export const COLLECTION_LIMIT = 200;
export const DETAIL_LIMIT = 20;
export const DEFAULT_CONFIG: YouTubeConfig = {
  channels: [],
  topics: [],
  country: 'US',
  language: 'en',
};

export function youtubeEnabled(): boolean {
  return process.env.NEXT_PUBLIC_YOUTUBE_ENABLED !== 'false';
}

export function youtubeRequestLimit(): number {
  const configured = Number(process.env.YOUTUBE_DAILY_REQUEST_LIMIT || 200);
  return Number.isFinite(configured) ? Math.max(10, Math.min(1000, Math.floor(configured))) : 200;
}

export function validateYouTubeConfig(value: unknown): YouTubeConfig {
  if (!value || typeof value !== 'object') throw new Error('Channel configuration is required.');
  const config = value as YouTubeConfig;
  if (!Array.isArray(config.channels) || config.channels.length < 1 || config.channels.length > 10)
    throw new Error('Connect between one and ten channels.');
  if (
    config.channels.some(
      channel => !channel || typeof channel !== 'object' || typeof channel.id !== 'string',
    )
  )
    throw new Error('Resolve each channel before saving.');
  if (!config.channels.some(channel => channel.role === 'owned'))
    throw new Error('Add at least one owned channel.');
  if (new Set(config.channels.map(channel => channel.id)).size !== config.channels.length)
    throw new Error('A channel can only be added once.');
  for (const channel of config.channels) {
    if (!validYouTubeChannelId(channel.id) || !['owned', 'competitor'].includes(channel.role))
      throw new Error('Resolve each channel before saving.');
  }
  if (
    !Array.isArray(config.topics) ||
    config.topics.length > 5 ||
    config.topics.some(topic => typeof topic !== 'string' || !topic.trim() || topic.length > 200)
  )
    throw new Error('Add up to five topics, each under 200 characters.');
  if (
    !/^[A-Za-z]{2}$/.test(config.country) ||
    !/^[a-z]{2,3}(?:-[A-Za-z]{2,4})?$/.test(config.language)
  )
    throw new Error('Use a two-letter country and a valid language code.');
  return {
    ...config,
    channels: config.channels.map(channel => ({ ...channel })),
    topics: [...new Set(config.topics.map(topic => topic.trim()))],
    country: config.country.toUpperCase(),
  };
}
